/**
 * TrustedBrandsSubSection — Phase 18.15.5
 *
 * Beheert ``homepage_trusted_brands`` — de bewegende logo-strip op
 * de homepage. Gebruikt dezelfde Media Library als de Hero block
 * picker (pickMode) zodat logo's via /api/media/{id} worden gekoppeld.
 *
 * RBAC: Owner/Admin/Content Manager kunnen beheren (P_WEBSITE_EDIT),
 * Manager view-only, Medewerker geen edit.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import MediaLibrary from "./MediaLibrary";
import {
  ArrowDown,
  ArrowUp,
  AlertTriangle,
  ImagePlus,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_BRAND = {
  brand_name: "",
  logo_url: "",
  alt_text: "",
  link_url: "",
  note: "",
  sort_order: 100,
  is_active: true,
};

export default function TrustedBrandsSubSection() {
  const { hasPermission, user } = useAuth();
  const canEdit = hasPermission("website:edit");
  const isOwner = user?.role === "owner";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/trusted-brands");
      setRows(r.data?.brands || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () =>
    setEditing({
      ...EMPTY_BRAND,
      sort_order: (rows[rows.length - 1]?.sort_order || 0) + 10,
    });

  const startEdit = (row) => setEditing({ ...row });

  const save = async () => {
    if (!editing.brand_name?.trim()) {
      toast.error("Vul de naam van het merk in.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await adminApi.put(`/admin/trusted-brands/${editing.id}`, editing);
        toast.success("Merk bijgewerkt");
      } else {
        await adminApi.post("/admin/trusted-brands", editing);
        toast.success("Merk toegevoegd");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    if (!row.logo_url && !row.is_active) {
      toast.error("Eerst een logo koppelen voor je het merk activeert.");
      return;
    }
    try {
      await adminApi.put(`/admin/trusted-brands/${row.id}`, {
        is_active: !row.is_active,
      });
      toast.success(row.is_active ? "Merk gedeactiveerd" : "Merk geactiveerd");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Definitief verwijderen: ${row.brand_name}?`)) return;
    try {
      await adminApi.delete(`/admin/trusted-brands/${row.id}`);
      toast.success("Merk verwijderd");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const move = async (row, direction) => {
    const idx = rows.findIndex((r) => r.id === row.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= rows.length) return;
    const swap = rows[swapIdx];
    try {
      await adminApi.post("/admin/trusted-brands/reorder", {
        brands: [
          { id: row.id, sort_order: swap.sort_order },
          { id: swap.id, sort_order: row.sort_order },
        ],
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resolveLogo = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600 py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Logo's laden…
      </div>
    );
  }

  return (
    <div data-testid="trusted-brands-subsection">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-600 max-w-2xl">
          Beheer de bewegende logo-strip op de homepage ("Vertrouwd door
          duizenden klanten en bedrijven"). Logo's komen via de Media
          Library — upload of kies een afbeelding per merk.
        </p>
        {canEdit && !editing && (
          <Button
            onClick={startCreate}
            className="bg-[#082c50] hover:bg-[#1a5193] text-white rounded-none font-chivo font-bold uppercase text-xs"
            data-testid="trusted-brands-add-button"
          >
            <Plus className="w-4 h-4 mr-1" /> Nieuw merk
          </Button>
        )}
      </div>

      <div
        className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-5 flex items-start gap-3"
        data-testid="trusted-brands-hint"
      >
        <AlertTriangle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 leading-relaxed">
          <strong>Tip:</strong> upload bij voorkeur transparante PNG of
          SVG-look logo's met een witte/lichte achtergrond. Merken
          zonder logo worden op de publieke homepage automatisch
          verborgen, zodat je nooit een broken-image placeholder ziet.
        </div>
      </div>

      {editing && (
        <div
          className="bg-white border border-gray-200 rounded-md p-5 mb-5"
          data-testid="trusted-brands-edit-form"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-chivo font-black text-lg text-[#082c50]">
              {editing.id ? "Merk bewerken" : "Nieuw merk"}
            </h3>
            <button
              onClick={() => setEditing(null)}
              className="text-gray-500 hover:text-gray-800"
              aria-label="Sluiten"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">
                Merknaam *
              </Label>
              <Input
                value={editing.brand_name || ""}
                onChange={(e) =>
                  setEditing({ ...editing, brand_name: e.target.value })
                }
                className="rounded-none mt-1"
                placeholder="bol.com"
                data-testid="trusted-brand-input-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">
                Alt-tekst
              </Label>
              <Input
                value={editing.alt_text || ""}
                onChange={(e) =>
                  setEditing({ ...editing, alt_text: e.target.value })
                }
                className="rounded-none mt-1"
                placeholder="bol.com logo"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide font-bold mb-1.5 block">
                Logo
              </Label>
              {editing.logo_url ? (
                <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-md p-3">
                  <img
                    src={resolveLogo(editing.logo_url)}
                    alt={editing.alt_text || editing.brand_name}
                    className="h-12 w-auto object-contain bg-white border border-gray-200 rounded p-1"
                  />
                  <div className="flex-1 min-w-0 text-xs text-gray-600 break-all">
                    {editing.logo_url}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPickerOpen(true)}
                    disabled={!canEdit}
                    className="rounded-none"
                  >
                    Wijzig
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setEditing({ ...editing, logo_url: "", alt_text: "" })
                    }
                    disabled={!canEdit}
                    className="rounded-none text-red-600"
                  >
                    Verwijder
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setPickerOpen(true)}
                  disabled={!canEdit}
                  className="rounded-none"
                  data-testid="trusted-brand-pick-logo"
                >
                  <ImagePlus className="w-4 h-4 mr-1" /> Kies uit Media Library
                </Button>
              )}
              <p className="text-[11px] text-gray-500 mt-1">
                Upload eerst je logo in Media (Websitebeheer → Media), kies
                het daarna hier.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">
                Optionele link
              </Label>
              <Input
                value={editing.link_url || ""}
                onChange={(e) =>
                  setEditing({ ...editing, link_url: e.target.value })
                }
                className="rounded-none mt-1"
                placeholder="https://www.bol.com"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">
                Sorteervolgorde
              </Label>
              <Input
                type="number"
                value={editing.sort_order ?? 100}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    sort_order: Number(e.target.value) || 0,
                  })
                }
                className="rounded-none mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide font-bold">
                Interne notitie
              </Label>
              <Input
                value={editing.note || ""}
                onChange={(e) =>
                  setEditing({ ...editing, note: e.target.value })
                }
                className="rounded-none mt-1"
                placeholder="Bijv. contactpersoon, contract-detail (niet zichtbaar publiek)"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch
                checked={!!editing.is_active}
                onCheckedChange={(v) =>
                  setEditing({ ...editing, is_active: v })
                }
              />
              <Label className="text-sm">
                {editing.is_active
                  ? "Actief — zichtbaar in marquee"
                  : "Inactief — niet zichtbaar"}
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              className="rounded-none"
            >
              Annuleren
            </Button>
            <Button
              onClick={save}
              disabled={saving || !canEdit}
              className="bg-[#082c50] hover:bg-[#1a5193] text-white rounded-none font-chivo font-bold uppercase text-xs"
              data-testid="trusted-brands-save-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Opslaan
            </Button>
          </div>
        </div>
      )}

      {!rows.length ? (
        <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-600">
          Nog geen merken ingesteld. Klik op <strong>Nieuw merk</strong>.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          {rows.map((row, idx) => (
            <div
              key={row.id}
              className={`px-5 py-4 flex items-center gap-4 ${
                idx > 0 ? "border-t border-gray-100" : ""
              } ${!row.is_active ? "opacity-60" : ""}`}
              data-testid={`trusted-brand-row-${(row.brand_name || "").toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={() => move(row, "up")}
                  disabled={idx === 0 || !canEdit}
                  className="text-gray-400 hover:text-[#082c50] disabled:opacity-30"
                  aria-label="Omhoog"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => move(row, "down")}
                  disabled={idx === rows.length - 1 || !canEdit}
                  className="text-gray-400 hover:text-[#082c50] disabled:opacity-30"
                  aria-label="Omlaag"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-shrink-0 w-20 h-12 bg-gray-50 border border-gray-200 rounded flex items-center justify-center overflow-hidden">
                {row.logo_url ? (
                  <img
                    src={resolveLogo(row.logo_url)}
                    alt={row.alt_text || row.brand_name}
                    className="max-h-10 max-w-[80%] object-contain"
                  />
                ) : (
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">
                    Geen logo
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-chivo font-black text-[#082c50]">
                  {row.brand_name}
                </div>
                {row.note ? (
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {row.note}
                  </div>
                ) : null}
                {!row.is_active ? (
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                    Inactief
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <Switch
                  checked={!!row.is_active}
                  onCheckedChange={() => toggleActive(row)}
                  disabled={!canEdit}
                />
                <button
                  onClick={() => startEdit(row)}
                  disabled={!canEdit}
                  className="text-xs font-bold text-[#1a5193] hover:text-[#082c50] disabled:opacity-50"
                >
                  Bewerken
                </button>
                {isOwner ? (
                  <button
                    onClick={() => deleteRow(row)}
                    className="text-xs font-bold text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Media picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-[#082c50]">
              Kies logo uit Media Library
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Selecteer een bestaande afbeelding of upload eerst een nieuwe
              in Websitebeheer → Media.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <MediaLibrary
              pickMode
              onSelect={(item) => {
                if (!item) return;
                setEditing((prev) => ({
                  ...prev,
                  logo_url: item.url || "",
                  alt_text:
                    prev?.alt_text || item.alt_text || item.title || "",
                }));
                setPickerOpen(false);
                toast.success("Logo gekoppeld — vergeet niet 'Opslaan'.");
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
